import { RequestTypes } from 'legacy/lut/classes/models/onboarding/Onboarding'
import { useCallback, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { getUrlParam } from 'core-components/helpers/url'
import { registrationLogin } from 'onboarding/classes/helpers/registration/registrationLogin'
import { registrationLoginViaToken } from 'onboarding/classes/helpers/registration/registrationLoginViaToken'
import { registrationRedirect } from 'onboarding/classes/helpers/registration/registrationRedirect'

interface IUseRegistrationLogin {
  email: string
  requestType?: RequestTypes
  changeIsLoading: (flag: boolean) => void
  changeRequestWasRejected: (flag: boolean) => void
  changeWarningMessage: (message: string) => void
}

export const useRegistrationLogin = ({
  email,
  requestType,
  changeIsLoading,
  changeWarningMessage,
  changeRequestWasRejected,
}: IUseRegistrationLogin) => {
  const navigate = useNavigate()
  const token = getUrlParam('token')
  const requestId = getUrlParam('rid')

  const handleWarnings = useCallback(
    (warningMessage: string, requestWasRejected?: boolean) => {
      if (requestWasRejected) {
        changeRequestWasRejected(requestWasRejected)
      }

      changeWarningMessage(warningMessage)
      changeIsLoading(false)
    },
    [changeIsLoading, changeRequestWasRejected, changeWarningMessage]
  )

  const login = useCallback(async () => {
    changeIsLoading(true)

    const { redirectRequestId, allowRedirect, requestWasRejected, warningMessage } =
      await registrationLogin({
        token,
        requestId: Number(requestId),
        requestType: requestType,
        email,
      })

    if (warningMessage) {
      handleWarnings(warningMessage, requestWasRejected)
      return
    }

    await registrationRedirect({ redirectRequestId, email, navigate, allowRedirect })

    changeIsLoading(false)
  }, [changeIsLoading, email, handleWarnings, navigate, requestId, requestType, token])

  const loginViaTokenFromUrl = useCallback(
    async (token: string, requestId: number) => {
      changeIsLoading(true)
      const { redirectRequestId, allowRedirect, warningMessage, email } =
        await registrationLoginViaToken({
          token,
          requestId: Number(requestId),
        })

      if (warningMessage) {
        handleWarnings(warningMessage)
        return
      }

      if (!email) {
        handleWarnings('Registration is not exists')
        return
      }

      await registrationRedirect({ redirectRequestId, email, navigate, allowRedirect })

      changeIsLoading(false)
    },
    [changeIsLoading, handleWarnings, navigate]
  )

  useEffect(() => {
    if (token && requestId) {
      loginViaTokenFromUrl(token, requestId)
    }
  }, [loginViaTokenFromUrl, requestId, token])

  useEffect(() => {
    const onKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        login()
      }
    }
    window.addEventListener('keypress', onKeyPress)
    return () => {
      window.removeEventListener('keypress', onKeyPress)
    }
  }, [login])

  return { login }
}
